import { supabase } from '../supabase/client';

export interface StorageReference {
    bucket: string;
    path: string;
}

const STORAGE_URL_PATTERN =
    /\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/([^?]+)/;

export function parseStorageReference(
    value: string,
    fallbackBucket: string,
): StorageReference | null {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const match = trimmed.match(STORAGE_URL_PATTERN);
    if (match) {
        return {
            bucket: decodeURIComponent(match[1]),
            path: decodeURIComponent(match[2]),
        };
    }

    if (/^https?:\/\//i.test(trimmed)) return null;
    return {
        bucket: fallbackBucket,
        path: trimmed.replace(/^\/+/, ''),
    };
}

export async function resolveStorageUrl(
    value: string,
    fallbackBucket: string,
    expiresInSeconds = 3600,
): Promise<string> {
    const reference = parseStorageReference(value, fallbackBucket);
    if (!reference) return value;

    // The `images` bucket is public. Signing as a guest fails even when the
    // object is readable, which left A-Tag covers blank on QR / logged-out views.
    if (reference.bucket === 'images') {
        const { data } = supabase.storage.from(reference.bucket).getPublicUrl(reference.path);
        if (data?.publicUrl) return data.publicUrl;
    }

    const { data, error } = await supabase.storage
        .from(reference.bucket)
        .createSignedUrl(reference.path, expiresInSeconds);
    if (!error && data?.signedUrl) return data.signedUrl;

    if (reference.bucket === 'images') {
        const { data: pub } = supabase.storage.from(reference.bucket).getPublicUrl(reference.path);
        if (pub?.publicUrl) return pub.publicUrl;
    }

    throw error ?? new Error('Unable to authorize media');
}
